// Dissolving sprite overlay for dying units.
// Billboarded plane with a noise-based dissolve shader — burns away at the edges.
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';

interface DissolveUniforms {
  uTexture: { value: THREE.Texture };
  uProgress: { value: number };
  uTime: { value: number };
  uTint: { value: THREE.Color };
}

const VERTEX = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorldPos;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorldPos;
  uniform sampler2D uTexture;
  uniform float uProgress;
  uniform float uTime;
  uniform vec3 uTint;

  // Simple hash-based noise
  float hash(vec3 p) {
    float h = dot(p, vec3(127.1, 311.7, 74.7));
    return fract(sin(h) * 43758.5453);
  }

  float noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(mix(hash(i), hash(i + vec3(1,0,0)), f.x),
          mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y),
      mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x),
          mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y), f.z);
  }

  void main() {
    vec4 tex = texture2D(uTexture, vUv);

    // Multi-octave noise for organic dissolve pattern
    float n = noise(vWorldPos * 3.0 + uTime * 0.2) * 0.6
            + noise(vWorldPos * 6.0 - uTime * 0.15) * 0.3
            + noise(vWorldPos * 12.0 + uTime * 0.1) * 0.1;

    // Dissolve threshold — fragments with noise below progress are discarded
    float threshold = uProgress;
    float edge = 0.15; // width of the burn edge band

    if (n < threshold - edge) {
      discard;
    }

    // Burn edge glow between threshold-edge and threshold
    float edgeFactor = smoothstep(threshold - edge, threshold, n);
    vec3 burnColor = vec3(1.0, 0.3, 0.05); // orange-red
    vec3 edgeGlow = mix(burnColor * 1.5, tex.rgb * uTint, edgeFactor);

    // Fully dissolved edge
    if (n < threshold) {
      gl_FragColor = vec4(edgeGlow, tex.a * (1.0 - edgeFactor));
    } else {
      gl_FragColor = vec4(tex.rgb * uTint, tex.a);
    }
  }
`;

interface DissolveSpriteProps {
  textureUrl: string;
  position: [number, number, number];
  scale?: number;
  color?: string;
  onComplete?: () => void;
}

export function DissolveSprite({
  textureUrl,
  position: [px, py, pz],
  scale = 0.8,
  color = '#ffffff',
  onComplete,
}: DissolveSpriteProps) {
  const groupRef = useRef<THREE.Group>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const startTime = useRef(performance.now() / 1000);
  const completedRef = useRef(false);

  const texture = useMemo(() => {
    const tex = new THREE.TextureLoader().load(textureUrl);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.NearestFilter;
    tex.magFilter = THREE.NearestFilter;
    return tex;
  }, [textureUrl]);

  const uniforms = useMemo(
    () => ({
      uTexture: { value: texture },
      uProgress: { value: 0 },
      uTime: { value: 0 },
      uTint: { value: new THREE.Color(color) },
    }),
    [],
  );

  // Cylindrical billboarding + dissolve progress
  useFrame(({ camera }) => {
    const g = groupRef.current;
    if (!g) return;

    // Billboard toward camera
    const dx = camera.position.x - g.position.x;
    const dz = camera.position.z - g.position.z;
    g.rotation.y = Math.atan2(dx, dz);

    const elapsed = performance.now() / 1000 - startTime.current;
    // Dissolve over 0.6 seconds
    const DURATION = 0.6;
    const progress = Math.min(elapsed / DURATION, 1.0);

    const mat = materialRef.current!;
    const u = mat.uniforms as unknown as DissolveUniforms;
    u.uProgress.value = progress;
    u.uTime.value = elapsed;

    if (progress >= 1.0 && onComplete && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
  });

  const baseY = 0.8 / 2 + scale / 2;

  return (
    <group ref={groupRef} position={[px, 0, pz]}>
      <mesh position={[0, baseY, 0]}>
        <planeGeometry args={[scale * 0.7, scale]} />
        <shaderMaterial
          ref={materialRef}
          vertexShader={VERTEX}
          fragmentShader={FRAGMENT}
          uniforms={uniforms}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}
